/* Copyright (c) 2023-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable } from 'rxjs';

export interface HigMarqueeTileActionData {
    uri: string;
    label?: string;
    infoText?: string;
    position?: number;
}

export enum HigMarqueeTileType {
    FullBleedImage = 'FullBleedImage',
    PaddedImage = 'PaddedImage'
}

export interface HigMarqueeTile {
    additionalSubtitle?: string;
    action?: HigMarqueeTileActionData;
    body?: string;
    fallbackImage?: string;
    heroImage?: string;
    marqueeHeroImage?: string;
    id?: string;
    logoImage?: string;
    promoId?: string;
    promoTag?: string;
    subtitle?: string;
    telemetryId?: string;
    telemetryAdditionalInfo?: string;
    title?: string;
    type: HigMarqueeTileType;
}

export interface HigMarqueeOverlayConfig {
    contentOverlayType: HigMarqueeTileType;
}

export interface HigMarqueeSwitchableImageConfig {
    alternate: HigMarqueeTileType;
    breakpointPx: number;
    default: HigMarqueeTileType;
}

export interface HigMarqueeImageConfig {
    customBackground: boolean;
    disableMask: boolean;
    formatImages: boolean;
    globalFallbackImageUrl: string;
    globalFallbackImageType: HigMarqueeTileType;
    switchableImageConfig: HigMarqueeSwitchableImageConfig;
}

export interface HigMarqueeGradientConfig {
    leftEdge: boolean;
    leftMerge: boolean;
    bottomEdge: boolean;
    background: boolean;
    backgroundOverhang: boolean;
}

export interface HigMarqueeAppMetadata {
    app: HigMarqueeTile;
    imageType: HigMarqueeTileType;
    isVisible: boolean;
    isShowingFallbackImage: boolean;
    gradients: HigMarqueeGradientConfig;
    contentOverlayType: HigMarqueeTileType;
}

export interface HigMarqueeActionClickEvent extends MouseEvent {
    action: HigMarqueeTileActionData;
    position?: number;
    id?: string;
}

export interface HigMarqueeScrollEvent {
    position: number;
    autoScrolled?: boolean;
}

export interface HigMarqueeViewAppDetailsEvent extends Event {
    app: HigMarqueeTile;
}

export interface HigMarqueeImageFetcherResult {
    imageUrl: string;
    metadata?: HigMarqueeImageMetadata;
}

export interface HigMarqueeImageMetadata {
    colors?: { left?: string; right?: string; bottom?: string };
}

export interface HigMarqueeImageFetcher {
    fetchImage(url: string): Observable<HigMarqueeImageFetcherResult>;
}

export const DefaultHigMarqueeFallbackImage = 'assets/img/Marquee_Hero_Image_Fallback.webp';
